// Arbre radial de descendance (sunburst) en SVG natif : l'ancêtre-racine au
// centre, ses descendants en anneaux concentriques qui rayonnent vers
// l'extérieur. Chaque personne = un secteur d'arc dont l'ouverture est
// proportionnelle à son nombre de descendants terminaux (feuilles).
//   - angle 360 → rond complet ; 180 → demi-cercle (éventail de descendance).
//   - rootId null → toute la famille (les troncs se partagent le disque).
// forwardRef sur le <svg> pour l'export vectoriel.

import React, { forwardRef } from 'react';
import { Membre, Relation } from '../../types';
import { getDescendants, getRootIds, DescendantNode } from '../../utils/lineage';
import { StylePreset, sectorPath, polar, resolveBg, memberYears, accentStroke } from './chartCommon';

export interface RadialDescendantChartProps {
  rootId: number | null;      // null = toute la famille
  membres: Membre[];
  relations: Relation[];
  style: StylePreset;
  isDark: boolean;
  maxGen: number;
  angle: 180 | 360;
}

const RING_W = 66;

interface Wedge { n: DescendantNode; a0: number; a1: number; gen: number; }

const RadialDescendantChart = forwardRef<SVGSVGElement, RadialDescendantChartProps>(function RadialDescendantChart(
  { rootId, membres, relations, style, isDark, maxGen, angle }, ref,
) {
  const rootIds = rootId == null ? getRootIds(membres, relations) : [rootId];
  const roots: DescendantNode[] = [];
  rootIds.forEach(rid => { const t = getDescendants(rid, membres, relations, maxGen); if (t) roots.push(t); });

  // Nombre de feuilles sous un nœud (largeur angulaire proportionnelle).
  const leaves = (n: DescendantNode): number =>
    n.children.length ? n.children.reduce((s, c) => s + leaves(c), 0) : 1;

  const full360 = angle === 360;
  const A0 = full360 ? -90 : 180;
  const A1 = full360 ? 270 : 360;

  let maxDepth = 0;
  (function depth(nodes: DescendantNode[], d: number) {
    nodes.forEach(n => { maxDepth = Math.max(maxDepth, d); depth(n.children, d + 1); });
  })(roots, 0);

  const R = (maxDepth + 1) * RING_W;
  const pad = 18;
  const cx = R + pad, cy = R + pad;
  const W = 2 * (R + pad);
  const H = full360 ? 2 * (R + pad) : R + 2 * pad;

  const totalLeaves = roots.reduce((s, r) => s + leaves(r), 0) || 1;
  const singleRootFull = roots.length === 1 && full360;

  const wedges: Wedge[] = [];
  const assign = (n: DescendantNode, a0: number, a1: number, gen: number) => {
    wedges.push({ n, a0, a1, gen });
    if (n.children.length) {
      const tot = n.children.reduce((s, c) => s + leaves(c), 0) || 1;
      let aa = a0;
      n.children.forEach(c => {
        const sp = (a1 - a0) * leaves(c) / tot;
        assign(c, aa, aa + sp, gen + 1);
        aa += sp;
      });
    }
  };
  let a = A0;
  roots.forEach(r => { const sp = (A1 - A0) * leaves(r) / totalLeaves; assign(r, a, a + sp, 0); a += sp; });

  const accent = accentStroke(style);
  const shapes: React.ReactNode[] = [];
  const labels: React.ReactNode[] = [];

  wedges.forEach(({ n, a0, a1, gen }, i) => {
    const m: Membre = n.membre;
    const ri = gen === 0 ? 0 : gen * RING_W;
    const ro = (gen + 1) * RING_W;
    const isCenterDisk = singleRootFull && gen === 0;
    const stroke = gen === 0 ? accent : style.sectorStroke;
    const strokeW = gen === 0 ? 2.5 : style.sectorStrokeW;
    const key = `w${i}`;

    if (isCenterDisk) {
      shapes.push(<circle key={key} cx={cx} cy={cy} r={ro} fill={style.sectorFill(0)} stroke={stroke} strokeWidth={strokeW} />);
    } else {
      shapes.push(<path key={key} d={sectorPath(cx, cy, ri, ro, a0, a1)} fill={style.sectorFill(gen)} stroke={stroke} strokeWidth={strokeW} />);
    }

    // Libellé : centré si disque, sinon radial — masqué si le secteur est trop étroit.
    const am = (a0 + a1) / 2;
    const rm = gen === 0 ? (isCenterDisk ? 0 : ro * 0.55) : (ri + ro) / 2;
    const arcLen = Math.max(rm, RING_W) * (a1 - a0) * Math.PI / 180;
    const sub = memberYears(m);

    if (isCenterDisk) {
      labels.push(
        <text key={`${key}n`} x={cx} y={cy - 2} fill={style.sectorText} fontFamily={style.font}
          fontSize={15} fontWeight={style.weight} textAnchor="middle">{m.prenom}</text>,
        sub ? <text key={`${key}s`} x={cx} y={cy + 13} fill={style.sectorText} fontFamily={style.font}
          fontSize={10.5} fontWeight={style.weightSub} textAnchor="middle" opacity={0.9}
          fontStyle={style.italicSub ? 'italic' : undefined}>{sub}</text> : null,
      );
    } else if (arcLen > 24) {
      const [tx, ty] = polar(cx, cy, rm, am);
      let rot = am; if (rot > 90 && rot < 270) rot += 180;
      const fs = gen >= 4 ? 8.5 : gen >= 2 ? 9.5 : 11;
      labels.push(
        <g key={`${key}g`} transform={`translate(${tx} ${ty}) rotate(${rot})`}>
          <text fill={style.sectorText} fontFamily={style.font} fontSize={fs}
            fontWeight={style.weight} textAnchor="middle" dy={sub && arcLen > 46 ? -1 : 3}>{m.prenom}</text>
          {sub && arcLen > 46 && (
            <text fill={style.sectorText} fontFamily={style.font} fontSize={fs - 1.5}
              fontWeight={style.weightSub} textAnchor="middle" dy={fs} opacity={0.85}
              fontStyle={style.italicSub ? 'italic' : undefined}>{sub}</text>
          )}
        </g>,
      );
    }
  });

  const bg = resolveBg(style, isDark);
  if (!wedges.length) {
    return (
      <svg ref={ref} width={200} height={120} viewBox="0 0 200 120" xmlns="http://www.w3.org/2000/svg">
        <rect x={0} y={0} width={200} height={120} fill={bg} />
        <text x={100} y={64} textAnchor="middle" fontFamily={style.font} fontSize={13} fill="#888">Aucun membre</text>
      </svg>
    );
  }

  return (
    <svg ref={ref} width={Math.ceil(W)} height={Math.ceil(H)} viewBox={`0 0 ${Math.ceil(W)} ${Math.ceil(H)}`}
      xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Arbre radial de descendance">
      <rect x={0} y={0} width={Math.ceil(W)} height={Math.ceil(H)} fill={bg} />
      {shapes}
      {labels}
    </svg>
  );
});

export default RadialDescendantChart;
